import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { Pill } from '../components/Pill';
import { btnGhost, btnPrimary } from '../components/buttons';
import { adminApi } from '../api/admin';
import type { FeatureRequestAdminDto, FeatureRequestStatus } from '../api/admin';
import { fmtDate } from '../lib/format';
import type { ApiError } from '../api/client';

type Filter = FeatureRequestStatus | 'all';

/**
 * Moderation queue for the iOS "What to build next?" board. Requests are
 * born pending (invisible to other users); approve to publish, mark
 * planned/completed to surface the corresponding badge in the app.
 */
export function FeatureRequestsScreen() {
  const [filter, setFilter] = useState<Filter>('pending');
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const requests = useQuery({
    queryKey: ['admin', 'feature-requests', filter],
    queryFn: () => adminApi.listFeatureRequests(filter === 'all' ? undefined : filter),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'feature-requests'] });
    setActionError(null);
  };

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'approved' | 'rejected' | 'planned' | 'completed' }) =>
      adminApi.setFeatureRequestStatus(id, status),
    onSuccess: invalidate,
    onError: (err: ApiError) => setActionError(err?.message ?? 'Update failed'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminApi.deleteFeatureRequest(id),
    onSuccess: invalidate,
    onError: (err: ApiError) => setActionError(err?.message ?? 'Delete failed'),
  });

  const rows = requests.data ?? [];

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 18, maxWidth: 1280 }}>
      <Card padding={0}>
        <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', gap: 2, background: 'var(--bg-sunken)', padding: 3, borderRadius: 'var(--r-pill)' }}>
            {(['pending', 'approved', 'planned', 'completed', 'rejected', 'all'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--r-pill)',
                  fontSize: 12,
                  fontWeight: 500,
                  background: filter === f ? 'var(--bg-elev)' : 'transparent',
                  color: filter === f ? 'var(--ink)' : 'var(--ink-3)',
                  boxShadow: filter === f ? 'var(--shadow-card)' : 'none',
                  textTransform: 'capitalize',
                }}
              >
                {f}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)' }}>
            {rows.length} {filter} request{rows.length === 1 ? '' : 's'}
          </div>
        </div>

        {actionError && (
          <div
            style={{
              margin: 16,
              fontSize: 12.5,
              color: 'var(--bad)',
              background: 'color-mix(in oklab, var(--bad) 10%, var(--bg-elev))',
              border: '1px solid color-mix(in oklab, var(--bad) 25%, transparent)',
              padding: '8px 12px',
              borderRadius: 'var(--r-sm)',
            }}
          >
            {actionError}
          </div>
        )}

        {rows.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
            {requests.isLoading ? 'Loading…' : filter === 'pending' ? 'Nothing in the queue 🎉' : 'No requests match this filter.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 1, background: 'var(--line)' }}>
            {rows.map((r) => (
              <RequestCard
                key={r.id}
                request={r}
                busy={setStatus.isPending || remove.isPending}
                onSetStatus={(status) => setStatus.mutate({ id: r.id, status })}
                onDelete={() => {
                  if (window.confirm(`Delete "${r.title}" permanently? Votes are removed too.`)) {
                    remove.mutate(r.id);
                  }
                }}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function RequestCard({
  request: r,
  busy,
  onSetStatus,
  onDelete,
}: {
  request: FeatureRequestAdminDto;
  busy: boolean;
  onSetStatus: (status: 'approved' | 'rejected' | 'planned' | 'completed') => void;
  onDelete: () => void;
}) {
  return (
    <div style={{ background: 'var(--bg-elev)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{r.title}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-2)' }}>▲ {r.voteCount}</span>
          <StatusPill status={r.status} />
        </div>
      </div>
      <div>
        {r.details && <div style={{ fontSize: 13, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>{r.details}</div>}
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 6 }}>
          {r.email ? <>contact: <span style={{ fontFamily: 'var(--font-mono)' }}>{r.email}</span> · </> : null}
          submitted {fmtDate(r.createdAt)}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {r.status === 'pending' && (
          <>
            <button style={{ ...btnPrimary, flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => onSetStatus('approved')}>
              Approve
            </button>
            <button
              style={{ ...btnGhost, flex: 1, justifyContent: 'center', color: 'var(--bad)', borderColor: 'color-mix(in oklab, var(--bad) 30%, var(--line))' }}
              disabled={busy}
              onClick={() => onSetStatus('rejected')}
            >
              Reject
            </button>
          </>
        )}
        {(r.status === 'approved' || r.status === 'planned') && (
          <>
            {r.status === 'approved' && (
              <button style={{ ...btnGhost, flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => onSetStatus('planned')}>
                Mark planned
              </button>
            )}
            <button style={{ ...btnPrimary, flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => onSetStatus('completed')}>
              Mark completed
            </button>
          </>
        )}
        <button
          style={{ ...btnGhost, justifyContent: 'center', color: 'var(--bad)', borderColor: 'color-mix(in oklab, var(--bad) 30%, var(--line))' }}
          disabled={busy}
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: FeatureRequestStatus }) {
  if (status === 'approved') return <Pill tone="good" size="sm">Approved</Pill>;
  if (status === 'pending') return <Pill tone="warn" size="sm">Pending</Pill>;
  if (status === 'planned') return <Pill tone="warn" size="sm">Planned</Pill>;
  if (status === 'completed') return <Pill tone="good" size="sm">Completed</Pill>;
  return <Pill tone="bad" size="sm">Rejected</Pill>;
}
